import {
  countryDisplayName,
  formatAmountWithUnit,
  type PublicShippingDestination,
  SHIPPING_ADDRESS_FIELD_MAX,
  SHIPPING_ADDRESS_FIELDS,
  type ShippingAddress,
  type ShippingAddressValidation,
  US_STATE_OPTIONS,
} from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { useState } from "react";

import { useCurrency } from "../lib/currency.js";
import {
  type AddressDraft,
  addressLines,
  changedFields,
  draftIssue,
  draftToAddress,
  regionMode,
  shippingErrorMessage,
  toDraft,
  useSaveShippingAddress,
  useValidateShippingAddress,
} from "../lib/shippingAddress.js";
import { useTerminology } from "../lib/terminology.js";
import { ErrorBanner } from "./ErrorBanner.js";

const US_STATE_LABELS = Object.fromEntries(US_STATE_OPTIONS.map((s) => [s.code, s.name]));

/** The rows the form lays out, in boxed's order: two names, the street, country and region, city and postal code, phone. */
const ROWS: (keyof ShippingAddress)[][] = [
  ["firstName", "lastName"],
  ["addressLine1"],
  ["addressLine2"],
  ["country", "region"],
  ["locality", "postalCode"],
  ["phoneNumber"],
];

const LABELS = Object.fromEntries(SHIPPING_ADDRESS_FIELDS.map((f) => [f.key, f.label])) as Record<
  keyof ShippingAddress,
  string
>;
const REQUIRED = new Set(SHIPPING_ADDRESS_FIELDS.filter((f) => f.required).map((f) => f.key));

type Step =
  | { kind: "form" }
  | { kind: "confirm"; entered: ShippingAddress; verdict: ShippingAddressValidation };

/**
 * The shopper's address form (boxed's AddressForm + ConfirmAddressModal): the country is one of
 * the store's destinations, a US region is a state, and submitting first asks address validation.
 * A clean verdict saves at once; a suggestion is shown beside what was typed to accept or keep;
 * an address the validator rejects can still be saved, and the server's own country check has
 * the last word.
 */
export function ShippingAddressDialog({
  open,
  onOpenChange,
  address,
  destinations,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  address: ShippingAddress | null;
  destinations: PublicShippingDestination[];
}) {
  const currency = useCurrency();
  const { item } = useTerminology();
  const validate = useValidateShippingAddress();
  const save = useSaveShippingAddress();
  const [draft, setDraft] = useState<AddressDraft>(() => toDraft(address));
  const [step, setStep] = useState<Step>({ kind: "form" });
  const [error, setError] = useState<string | null>(null);

  const countryLabels = Object.fromEntries(
    destinations.map((d) => [d.countryCode, countryDisplayName(d.countryCode)]),
  );
  const destination = destinations.find((d) => d.countryCode === draft.country);
  const candidate = draftToAddress(draft);
  const issue = draftIssue(draft);
  const busy = validate.isPending || save.isPending;

  const reset = (next: boolean) => {
    if (next) {
      setDraft(toDraft(address));
      setStep({ kind: "form" });
      setError(null);
    }
    onOpenChange(next);
  };

  const commit = (toSave: ShippingAddress) => {
    setError(null);
    save.mutate(toSave, {
      onSuccess: () => onOpenChange(false),
      onError: (err) => {
        setStep({ kind: "form" });
        setError(shippingErrorMessage(err));
      },
    });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!candidate || issue) return;
    setError(null);
    validate.mutate(candidate, {
      onSuccess: (verdict) => {
        if (verdict.status === "valid" || verdict.status === "unverified") commit(candidate);
        else setStep({ kind: "confirm", entered: candidate, verdict });
      },
      onError: (err) => setError(shippingErrorMessage(err)),
    });
  };

  const field = (key: keyof ShippingAddress) => {
    const id = `shipping-${key}`;
    if (key === "country") {
      return (
        <Select
          value={draft.country || null}
          items={countryLabels}
          onValueChange={(value) =>
            // A new country empties the region: a state code means nothing elsewhere.
            setDraft({ ...draft, country: value ?? "", region: "" })
          }
        >
          <SelectTrigger id={id} className="w-full" aria-label={LABELS[key]}>
            <SelectValue placeholder="Choose a country" />
          </SelectTrigger>
          <SelectContent>
            {destinations.map((d) => (
              <SelectItem key={d.countryCode} value={d.countryCode}>
                {countryLabels[d.countryCode]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (key === "region" && regionMode(draft.country) === "us-state") {
      return (
        <Select
          value={draft.region || null}
          items={US_STATE_LABELS}
          onValueChange={(value) => setDraft({ ...draft, region: value ?? "" })}
        >
          <SelectTrigger id={id} className="w-full" aria-label="State">
            <SelectValue placeholder="Choose a state" />
          </SelectTrigger>
          <SelectContent>
            {US_STATE_OPTIONS.map((s) => (
              <SelectItem key={s.code} value={s.code}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    return (
      <Input
        id={id}
        value={draft[key]}
        maxLength={SHIPPING_ADDRESS_FIELD_MAX}
        required={REQUIRED.has(key)}
        autoComplete={AUTOCOMPLETE[key]}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
      />
    );
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="sm:max-w-lg">
        {step.kind === "form" ? (
          <>
            <DialogHeader>
              <DialogTitle>Shipping address</DialogTitle>
              <DialogDescription>
                Where your {item.plural} ship. Everything except the second line and the phone
                number is required.
              </DialogDescription>
            </DialogHeader>
            <form className="grid gap-3" onSubmit={submit}>
              {ROWS.map((row) => (
                <div key={row.join("-")} className="grid gap-3 sm:grid-cols-2">
                  {row.map((key) => (
                    <div
                      key={key}
                      className={`grid gap-1.5 ${row.length === 1 ? "sm:col-span-2" : ""}`}
                    >
                      <Label htmlFor={`shipping-${key}`}>
                        {key === "region" && regionMode(draft.country) === "us-state"
                          ? "State"
                          : LABELS[key]}
                      </Label>
                      {field(key)}
                    </div>
                  ))}
                </div>
              ))}
              {destination && (
                <p className="text-xs text-muted-foreground">
                  Shipping to {countryLabels[destination.countryCode]}:{" "}
                  {destination.costCents === 0
                    ? "free"
                    : formatAmountWithUnit(destination.costCents, currency)}{" "}
                  · {destination.deliveryEstimate}
                </p>
              )}
              {issue && <p className="text-xs text-destructive">{issue}</p>}
              {error && <ErrorBanner>{error}</ErrorBanner>}
              <DialogFooter>
                <DialogClose render={<Button type="button" variant="outline" />}>
                  Cancel
                </DialogClose>
                <Button type="submit" disabled={!candidate || issue !== null || busy}>
                  {validate.isPending ? "Checking…" : save.isPending ? "Saving…" : "Save address"}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <ConfirmStep
            entered={step.entered}
            verdict={step.verdict}
            busy={busy}
            error={error}
            onEdit={() => setStep({ kind: "form" })}
            onSave={commit}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

const AUTOCOMPLETE: Partial<Record<keyof ShippingAddress, string>> = {
  firstName: "given-name",
  lastName: "family-name",
  addressLine1: "address-line1",
  addressLine2: "address-line2",
  locality: "address-level2",
  region: "address-level1",
  postalCode: "postal-code",
  phoneNumber: "tel",
};

/**
 * What address validation said, and the choice it leaves: a suggestion to accept or decline, or
 * an address it could not find, which the shopper may save anyway.
 */
function ConfirmStep({
  entered,
  verdict,
  busy,
  error,
  onEdit,
  onSave,
}: {
  entered: ShippingAddress;
  verdict: ShippingAddressValidation;
  busy: boolean;
  error: string | null;
  onEdit: () => void;
  onSave: (address: ShippingAddress) => void;
}) {
  const suggested = verdict.correctedAddress ?? null;
  const changed = suggested ? new Set(changedFields(entered, suggested)) : new Set<string>();
  const hasSuggestion = suggested !== null && changed.size > 0;
  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {hasSuggestion ? "Use the suggested address?" : "Address not found"}
        </DialogTitle>
        <DialogDescription>
          {hasSuggestion
            ? "Address validation suggests a small correction. Pick the version to save."
            : "Address validation could not confirm this address. Check it, or save it as typed."}
        </DialogDescription>
      </DialogHeader>
      <div className={`grid gap-4 text-sm ${hasSuggestion ? "sm:grid-cols-2" : ""}`}>
        <AddressBlock title="You entered" address={entered} />
        {hasSuggestion && suggested && (
          <AddressBlock title="Suggested" address={suggested} highlight={changed} />
        )}
      </div>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <DialogFooter>
        <Button type="button" variant="outline" disabled={busy} onClick={onEdit}>
          Edit
        </Button>
        <Button
          type="button"
          variant={hasSuggestion ? "outline" : "default"}
          disabled={busy}
          onClick={() => onSave(entered)}
        >
          {hasSuggestion ? "Keep what I entered" : busy ? "Saving…" : "Save anyway"}
        </Button>
        {hasSuggestion && suggested && (
          <Button type="button" disabled={busy} onClick={() => onSave(suggested)}>
            {busy ? "Saving…" : "Use suggested"}
          </Button>
        )}
      </DialogFooter>
    </>
  );
}

function AddressBlock({
  title,
  address,
  highlight,
}: {
  title: string;
  address: ShippingAddress;
  highlight?: ReadonlySet<string>;
}) {
  // Which line carries which field, so a changed field can be marked.
  const lines: [string, string][] = [
    ["name", `${address.firstName} ${address.lastName}`],
    ["addressLine1", address.addressLine1],
    ...(address.addressLine2
      ? ([["addressLine2", address.addressLine2]] as [string, string][])
      : []),
    ["locality", `${address.locality}, ${address.region} ${address.postalCode}`],
    ["country", countryDisplayName(address.country)],
  ];
  const changedLine = (key: string) =>
    key === "locality"
      ? ["locality", "region", "postalCode"].some((k) => highlight?.has(k))
      : highlight?.has(key);
  return (
    <div className="rounded-md border p-3">
      <p className="mb-1 text-xs font-medium text-muted-foreground">{title}</p>
      <address className="grid gap-0.5 not-italic">
        {lines.map(([key, text]) => (
          <span key={key} className={changedLine(key) ? "font-medium text-primary" : undefined}>
            {text}
          </span>
        ))}
      </address>
      {addressLines(address).length > lines.length && address.phoneNumber && (
        <p className="mt-1 text-xs text-muted-foreground">{address.phoneNumber}</p>
      )}
    </div>
  );
}
